import type SimpleBarClass from "simplebar";

import { IS_ACTIVE } from "../_defs";
import { disableScroll } from "../functions/_disableScroll";
import { enableScroll } from "../functions/_enableScroll";
import { getTarget } from "../functions/_getTarget";

import { mobileQuery } from "./_mobileQuery";

type Dialog = {
  trigger: HTMLElement;
  target: HTMLElement;
  window: HTMLElement | null;
  scrollBox: HTMLElement | null;
  scrollbar: SimpleBarClass | null;
  used: boolean;
  opened: boolean;
  busy: boolean;
};

type DialogParameter = {
  before: gsap.TweenVars;
  after: gsap.TweenVars;
};

const DIALOGS: { [key: string]: Dialog } = {};
const DURATION = 0.2;
const OVERLAY = document.createElement("div");

let busy = false;

const PARAMETERS_PC: { [key: string]: DialogParameter } = {
  default: {
    before: { opacity: 0, x: 0, y: 20, scale: 0.98 },
    after: { opacity: 1, x: 0, y: 0, scale: 1 }
  },
  prev: {
    before: { opacity: 0, x: -40, y: 0, scale: 0.98 },
    after: { opacity: 1, x: 0, y: 0, scale: 1 }
  },
  next: {
    before: { opacity: 0, x: 40, y: 0, scale: 0.98 },
    after: { opacity: 1, x: 0, y: 0, scale: 1 }
  }
};

const PARAMETERS_SP: { [key: string]: DialogParameter } = {
  default: {
    before: { opacity: 0, x: 0, y: 0, scale: 1 },
    after: { opacity: 1, x: 0, y: 0, scale: 1 }
  },
  prev: {
    before: { opacity: 0, x: -20, y: 0, scale: 0.98 },
    after: { opacity: 1, x: 0, y: 0, scale: 1 }
  },
  next: {
    before: { opacity: 0, x: 20, y: 0, scale: 0.98 },
    after: { opacity: 1, x: 0, y: 0, scale: 1 }
  }
};

let current: Dialog | undefined;

const reverseEffect = (effect: string): string => {
  switch (effect) {
    case "prev":
      return "next";

    case "next":
      return "prev";

    default:
      return effect;
  }
};

const getParameter = (effect?: string): DialogParameter => {
  const p = mobileQuery.matches ? PARAMETERS_SP : PARAMETERS_PC;

  return p[effect ?? ""] ?? p.default;
};

const adjustScrollbar = (_scrollbar: SimpleBarClass): void => {
  // scrollbar.el.scrollTop = 2;
  // setTimeout(() => {
  //   scrollbar.el.scrollTop = 0;
  // }, 15);
};

const close = (
  dialog: Dialog,
  effect?: string,
  keepOverlay?: boolean,
  callback?: () => void
): void => {
  if (busy || dialog.busy || !dialog.opened) return;

  busy = true;
  dialog.busy = true;

  setTimeout(() => {
    busy = false;
    dialog.busy = false;
    dialog.opened = false;
    dialog.target.classList.remove(IS_ACTIVE);

    enableScroll(dialog.scrollBox);

    current = undefined;
    callback?.();
  }, DURATION * 1000);

  if (!keepOverlay) {
    gsap.fromTo(
      OVERLAY,
      { opacity: 1, display: "block" },
      { opacity: 0, display: "none", duration: DURATION }
    );
  }

  if (dialog.window) {
    const parameter = getParameter(effect);

    gsap.fromTo(dialog.window, parameter.after, {
      ...parameter.before,
      duration: DURATION
    });
  }

  dialog.target.querySelectorAll<HTMLVideoElement>("video").forEach((video) => {
    video.pause();
    video.currentTime = 0;
  });
};

const open = (dialog: Dialog, effect: string, keepOverlay?: boolean): void => {
  if (busy || dialog.busy || dialog.opened) return;

  if (current && current !== dialog) {
    close(current, reverseEffect(effect), true, () =>
      open(dialog, effect, true)
    );

    return;
  }

  busy = true;
  dialog.busy = true;
  dialog.target.classList.add(IS_ACTIVE);

  setTimeout(() => {
    busy = false;
    dialog.busy = false;
    dialog.opened = true;
    current = dialog;
  }, DURATION * 1000);

  disableScroll(dialog.scrollBox);

  if (!keepOverlay) {
    gsap.fromTo(
      OVERLAY,
      { opacity: 0, display: "none" },
      { opacity: 1, display: "block", duration: DURATION }
    );
  }

  if (dialog.window) {
    const parameter = getParameter(effect);

    gsap.fromTo(dialog.window, parameter.before, {
      ...parameter.after,
      duration: DURATION,
      onStart() {
        if (dialog.scrollbar) {
          dialog.scrollbar.recalculate();

          if (!dialog.used) {
            adjustScrollbar(dialog.scrollbar);
            dialog.used = true;
          }
        }
      }
    });
  }
};

OVERLAY.className = "c-dialog__overlay";
OVERLAY.addEventListener("click", () => {
  if (current) close(current);
});
document.body.appendChild(OVERLAY);

export const getDialog = (
  idOrElement: string | HTMLElement
): Dialog | undefined => {
  if (idOrElement instanceof HTMLElement) {
    return getDialog(idOrElement.getAttribute("id") ?? "");
  }

  return DIALOGS[idOrElement];
};

export const openDialog = (
  idOrElement: string | HTMLElement,
  effect: string
): void => {
  const dialog = getDialog(idOrElement);

  if (dialog) open(dialog, effect);
};

export const setupDialog = (): void => {
  document
    .querySelectorAll<HTMLElement>("[data-dialog-trigger]")
    .forEach((trigger) => {
      const target = getTarget(trigger);

      if (!(target instanceof HTMLElement)) return;

      const id = target.getAttribute("id");

      if (id === null || id.length === 0) return;

      const body = target.querySelector<HTMLElement>("[data-dialog-body]");

      const dialog: Dialog = DIALOGS[id] ?? {
        trigger,
        target,
        window: target.querySelector<HTMLElement>("[data-dialog-window]"),
        scrollBox: body,
        scrollbar: body ? new SimpleBar(body, { autoHide: false }) : null,
        used: false,
        opened: false,
        busy: false
      };

      target
        .querySelectorAll("[data-dialog-close]")
        .forEach((e) => e.addEventListener("click", () => close(dialog)));

      trigger.addEventListener("click", (ev) => {
        ev.preventDefault();
        open(dialog, trigger.getAttribute("data-dialog-trigger") ?? "");
      });

      DIALOGS[id] = dialog;
    });
};

export const autoOpenDialog = (scrollId?: string): void => {
  const target = site.getTarget(window.location);

  if (!(target instanceof HTMLElement)) return;

  const id = target.getAttribute("id");

  if (id === null || id.length === 0) return;

  if (!target.classList.contains("c-dialog")) return;

  const list =
    scrollId !== undefined ? document.getElementById(scrollId) : null;

  if (list) {
    list.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      site.openDialog(id, "");
    }, 500);
  } else {
    site.openDialog(id, "");
  }
};
